import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Coins, Check, Zap, Star, Users, Brain,
  Loader2, AlertTriangle, Receipt, ArrowRight, Shield,
  TrendingUp, Clock, Sparkles, BarChart3
} from 'lucide-react';

const formatPrice = (paise) => new Intl.NumberFormat('en-IN').format(paise / 100);

const PLAN_META = {
  trial: { label: 'Free Trial', icon: Clock, badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  starter: { label: 'Starter', icon: Zap, badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  pro: { label: 'Pro', icon: Star, badge: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' },
};

export default function BillingPage() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPkg, setProcessingPkg] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [subRes, balRes, txnRes, brkRes, cfgRes] = await Promise.all([
        api.get('/subscription/status'),
        api.get('/credits/balance').catch(() => ({ data: null })),
        api.get('/credits/transactions?limit=20').catch(() => ({ data: [] })),
        api.get('/credits/usage-breakdown').catch(() => ({ data: [] })),
        api.get('/credits/config'),
      ]);
      setSubscription(subRes.data);
      setBalance(balRes.data);
      setTransactions(txnRes.data || []);
      setBreakdown(brkRes.data || []);
      setConfig(cfgRes.data);
    } catch {
      toast.error('Failed to load billing data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (document.getElementById('razorpay-script')) { resolve(true); return; }
      const script = document.createElement('script');
      script.id = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleBuyCredits = async (packageId) => {
    if (!user?.company_id) {
      toast.error('Billing is available for company accounts only');
      return;
    }
    setProcessingPkg(packageId);
    try {
      const orderRes = await api.post(`/credits/purchase?package_id=${packageId}`);
      const { order_id, amount, currency, key_id, credits } = orderRes.data;
      const loaded = await loadRazorpayScript();
      if (!loaded) { toast.error('Failed to load payment gateway'); setProcessingPkg(null); return; }

      const pkg = config?.packages?.[packageId] || {};
      const options = {
        key: key_id, amount, currency,
        name: 'VoyageOS',
        description: `${pkg.name || packageId} — ${credits} AI Credits`,
        order_id,
        handler: async function (response) {
          try {
            await api.post(`/credits/verify?razorpay_order_id=${response.razorpay_order_id}&razorpay_payment_id=${response.razorpay_payment_id}&razorpay_signature=${response.razorpay_signature}`);
            toast.success(`${credits} AI credits added to your account!`);
            fetchData();
          } catch { toast.error('Payment verification failed. Contact support.'); }
          setProcessingPkg(null);
        },
        modal: { ondismiss: () => { setProcessingPkg(null); toast.info('Payment cancelled'); } },
        prefill: { name: user?.name || '', email: user?.email || '' },
        notes: { company_id: user?.company_id || '', package_id: packageId, type: 'credit_purchase' },
        theme: { color: '#0E4F8F' }
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (r) => { toast.error(`Payment failed: ${r.error.description}`); setProcessingPkg(null); });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate purchase');
      setProcessingPkg(null);
    }
  };

  const currentPlan = subscription?.plan || 'trial';
  const planMeta = PLAN_META[currentPlan] || PLAN_META.trial;
  const PlanIcon = planMeta.icon;
  const trialExpired = subscription?.trial_expired;
  const packages = config?.packages || {};

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="billing-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const monthlyCredits = balance?.monthly_credits || subscription?.monthly_credits || 500;
  const creditsUsed = balance?.credits_used_this_cycle || 0;
  const monthlyRemaining = balance?.monthly_remaining || Math.max(0, monthlyCredits - creditsUsed);
  const purchasedRemaining = balance?.purchased_remaining || 0;
  const totalAvailable = balance?.total_available || (monthlyRemaining + purchasedRemaining);
  const usagePercent = monthlyCredits > 0 ? Math.min(100, Math.round((creditsUsed / monthlyCredits) * 100)) : 0;

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl" data-testid="billing-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground tracking-tight">Billing & Credits</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Manage your AI credits and view usage</p>
      </div>

      {/* Trial Warning */}
      {currentPlan === 'trial' && (
        <div className={`flex items-start gap-3 p-4 rounded-xl border ${trialExpired ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800' : 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800'}`}>
          <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${trialExpired ? 'text-red-500' : 'text-amber-500'}`} />
          <div>
            <p className={`text-sm font-medium ${trialExpired ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`}>
              {trialExpired
                ? 'Your 15-day free trial has expired. Subscribe to continue using VoyageOS.'
                : (() => {
                    if (!subscription?.trial_ends_at) return 'You are on a free trial.';
                    const h = Math.max(0, Math.floor((new Date(subscription.trial_ends_at) - new Date()) / 3600000));
                    return h > 24 ? `Your trial expires in ${Math.ceil(h / 24)} days.` : `Your trial expires in ${h} hours.`;
                  })()
              }
            </p>
            <p className="text-xs text-muted-foreground mt-1">Visit the <a href="/welcome#pricing" className="underline text-primary">pricing page</a> to subscribe.</p>
          </div>
        </div>
      )}

      {/* Current Plan + Credit Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border border-border bg-card" data-testid="plan-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <PlanIcon className="w-4 h-4 text-primary" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Current Plan</span>
            </div>
            <Badge className={`text-sm px-3 py-1 ${planMeta.badge}`} data-testid="current-plan-badge">
              {planMeta.label}
            </Badge>
            <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
              <span><Users className="w-3 h-3 inline mr-1" />{subscription?.max_users || 1} users</span>
              <span><Coins className="w-3 h-3 inline mr-1" />{monthlyCredits}/mo</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border bg-card" data-testid="credits-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <Coins className="w-4 h-4 text-amber-500" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Total Credits Available</span>
            </div>
            <p className="text-3xl font-heading font-bold text-foreground" data-testid="total-credits">{totalAvailable.toLocaleString('en-IN')}</p>
            <div className="flex items-center gap-3 mt-2 text-xs">
              <span className="text-blue-600">{monthlyRemaining} monthly</span>
              <span className="text-violet-600">{purchasedRemaining} purchased</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border bg-card" data-testid="usage-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Monthly Usage</span>
            </div>
            <p className="text-sm font-medium mb-2">{creditsUsed} / {monthlyCredits} credits used</p>
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${usagePercent > 80 ? 'bg-red-500' : usagePercent > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">{usagePercent}% used this cycle</p>
          </CardContent>
        </Card>
      </div>

      {/* Credit Top-Up Packages */}
      <div>
        <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" /> Buy AI Credits
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="credit-packages-crm">
          {Object.entries(packages).map(([id, pkg]) => (
            <Card key={id} className={`border hover:shadow-md transition-shadow ${id === 'growth' ? 'border-violet-300 dark:border-violet-700 ring-1 ring-violet-200/50' : 'border-border'}`} data-testid={`crm-pkg-${id}`}>
              <CardContent className="pt-5 pb-4">
                {id === 'growth' && <Badge className="bg-violet-100 text-violet-700 text-[10px] mb-2">Best Value</Badge>}
                <p className="font-heading font-bold text-base">{pkg.name}</p>
                <p className="text-2xl font-bold mt-1">{pkg.credits.toLocaleString('en-IN')} <span className="text-sm font-normal text-muted-foreground">credits</span></p>
                <p className="text-lg font-semibold text-foreground mt-0.5">₹{formatPrice(pkg.price)}</p>
                <p className="text-[10px] text-muted-foreground mt-1 mb-3">₹{(pkg.price / 100 / pkg.credits).toFixed(2)}/credit &middot; Valid 12 months</p>
                <Button
                  className="w-full gap-1.5"
                  variant={id === 'growth' ? 'default' : 'outline'}
                  disabled={!!processingPkg}
                  onClick={() => handleBuyCredits(id)}
                  data-testid={`buy-credits-${id}`}
                >
                  {processingPkg === id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
                  {processingPkg === id ? 'Processing...' : 'Buy Now'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Usage Breakdown */}
      {breakdown.length > 0 && (
        <Card className="border border-border bg-card" data-testid="usage-breakdown">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary" /> Usage Breakdown (This Cycle)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {breakdown.map(b => (
                <div key={b.operation} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{b.label}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">{b.count}x</span>
                    <span className="font-semibold tabular-nums">{b.total_credits} credits</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Transactions */}
      {transactions.length > 0 && (
        <Card className="border border-border bg-card" data-testid="transactions-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <Receipt className="w-4 h-4 text-primary" /> Recent Credit Transactions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {transactions.map(t => (
                <div key={t.id} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0 text-sm">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{t.description}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {new Date(t.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <span className={`font-semibold tabular-nums ${t.amount > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {t.amount > 0 ? '+' : ''}{t.amount}
                    </span>
                    <Badge variant="outline" className={`text-[10px] ${
                      t.type === 'purchase' ? 'border-emerald-300 text-emerald-600' :
                      t.type === 'refund' ? 'border-blue-300 text-blue-600' :
                      t.type === 'monthly_grant' ? 'border-violet-300 text-violet-600' :
                      'border-slate-300 text-slate-600'
                    }`}>
                      {t.type === 'deduction' ? 'used' : t.type === 'monthly_grant' ? 'monthly' : t.type}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50 border border-border">
        <Shield className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
        <div className="text-sm text-muted-foreground">
          <p>Monthly credits reset at each billing cycle. Purchased credits are valid for 12 months. Monthly credits are consumed first.</p>
          <p className="mt-1">To upgrade your subscription plan, visit the <a href="/welcome#pricing" className="underline text-primary">pricing page</a>.</p>
        </div>
      </div>
    </div>
  );
}
